package com.exura.pages.import

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.demofeatures.domain.model.FeatureName
import com.exura.features.demofeatures.domain.usecase.CheckFeatureEnabledUseCase
import com.exura.features.demosets.domain.model.DemoSet
import com.exura.features.demosets.domain.usecase.ImportPrivateDemoSetUseCase
import com.exura.features.demosets.domain.usecase.ImportPublicDemoSetUseCase
import com.exura.features.demosets.domain.usecase.ListDemoSetsUseCase
import com.exura.features.demosets.domain.usecase.PublishPlanDefinitionUseCase
import com.exura.features.firebase.domain.model.versioning.Version
import com.exura.features.plandefinition.domain.model.metadata.PlanDefinitionMetadata
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.core.PageContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

sealed interface ImportEvent {
    data object LoadDemoSets : ImportEvent
    data class ImportPrivateDemoSet(
        val accessCode: String,
        val onSuccess: (Int) -> Unit,
        val onFailure: (String) -> Unit
    ) : ImportEvent

    data class ImportPublicDemoSet(
        val demoSetId: String,
        val onSuccess: () -> Unit,
        val onFailure: (String) -> Unit
    ) : ImportEvent

    data class PublishPlan(
        val planId: String,
        val targetDemoId: String,
        val isPrivate: Boolean,
        val onSuccess: () -> Unit,
        val onFailure: (String) -> Unit
    ) : ImportEvent

    data class CheckPublishFeature(
        val onSuccess: (Boolean) -> Unit,
        val onFailure: (String) -> Unit
    ) : ImportEvent

    data class UpdateAccessCode(val accessCode: String) : ImportEvent
    data class UpdateSelectedPlan(val plan: Version<BasePlan, PlanDefinitionMetadata>) : ImportEvent
    data class UpdateTargetDemoId(val targetDemoId: String) : ImportEvent
    data class UpdateIsPrivate(val isPrivate: Boolean) : ImportEvent
}

data class ImportUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val demoSets: List<DemoSet> = emptyList(),
    val expandedDemoSetId: String? = null,
    val importingDemoSetId: String? = null,
    val showPublish: Boolean = false,
    val accessCode: String = "",
    val selectedPlan: Version<BasePlan, PlanDefinitionMetadata>? = null,
    val targetDemoId: String = "",
    val isPrivate: Boolean = false,
)

class ImportController(
    private val scope: CoroutineScope,
    private val context: PageContext,
    private val listDemoSetsUseCase: ListDemoSetsUseCase,
    private val importPublicDemoSetUseCase: ImportPublicDemoSetUseCase,
    private val importPrivateDemoSetUseCase: ImportPrivateDemoSetUseCase,
    private val publishPlanDefinitionUseCase: PublishPlanDefinitionUseCase,
    private val checkFeatureEnabledUseCase: CheckFeatureEnabledUseCase,
) {
    var uiState by mutableStateOf(ImportUiState())
        private set

    init {
        loadDemoSets()
        checkPublishFeature()
    }

    private fun checkPublishFeature() {
        scope.launch {
            checkFeatureEnabledUseCase(FeatureName.PLAN_PUBLISH)
                .onSuccess { enabled ->
                    uiState = uiState.copy(showPublish = enabled)
                }
                .onFailure { error ->
                    uiState = uiState.copy(showPublish = false)
                }
        }
    }

    fun loadDemoSets() {
        scope.launch {
            uiState = uiState.copy(isLoading = true, error = null)
            listDemoSetsUseCase()
                .onSuccess { demoSets ->
                    uiState = uiState.copy(isLoading = false, demoSets = demoSets)
                }
                .onFailure { error ->
                    uiState = uiState.copy(isLoading = false, error = error.message)
                }
        }
    }

    fun onImportPrivateDemoSet(accessCode: String, onSuccess: (Int) -> Unit, onFailure: (String) -> Unit) {
        scope.launch {
            uiState = uiState.copy(importingDemoSetId = "private", isLoading = true)
            importPrivateDemoSetUseCase(accessCode)
                .onSuccess {
                    uiState = uiState.copy(importingDemoSetId = null, isLoading = false)
                    onSuccess(it.count())
                }
                .onFailure { error ->
                    uiState = uiState.copy(importingDemoSetId = null, isLoading = false)
                    onFailure(error.message ?: "Unknown error")
                }
        }
    }

    fun onImportPublicDemoSet(demoSetId: String, onSuccess: () -> Unit, onFailure: (String) -> Unit) {
        scope.launch {
            uiState = uiState.copy(importingDemoSetId = demoSetId, isLoading = true)
            importPublicDemoSetUseCase(demoSetId)
                .onSuccess {
                    uiState = uiState.copy(importingDemoSetId = null, isLoading = false)
                    onSuccess()
                }
                .onFailure { error ->
                    uiState = uiState.copy(importingDemoSetId = null, isLoading = false)
                    onFailure(error.message ?: "Unknown error")
                }
        }
    }

    fun onPublishPlan(
        planId: String,
        targetDemoId: String,
        isPrivate: Boolean,
        onSuccess: () -> Unit,
        onFailure: (String) -> Unit
    ) {
        scope.launch {
            uiState = uiState.copy(isLoading = true)
            publishPlanDefinitionUseCase(planId, targetDemoId, isPrivate)
                .onSuccess {
                    uiState = uiState.copy(isLoading = false)
                    onSuccess()
                }
                .onFailure { error ->
                    uiState = uiState.copy(isLoading = false)
                    onFailure(error.message ?: "Unknown error")
                }
        }
    }

    fun onCheckPublishFeature(onSuccess: (Boolean) -> Unit, onFailure: (String) -> Unit) {
        scope.launch {
            checkFeatureEnabledUseCase(FeatureName.PLAN_PUBLISH)
                .onSuccess { enabled ->
                    uiState = uiState.copy(showPublish = enabled)
                    onSuccess(enabled)
                }
                .onFailure { error -> onFailure(error.message ?: "Unknown error") }
        }
    }

    fun onUpdateAccessCode(accessCode: String) {
        uiState = uiState.copy(accessCode = accessCode)
    }

    fun onUpdateSelectedPlan(plan: Version<BasePlan, PlanDefinitionMetadata>) {
        uiState = uiState.copy(selectedPlan = plan)
    }

    fun onUpdateTargetDemoId(targetDemoId: String) {
        uiState = uiState.copy(targetDemoId = targetDemoId)
    }

    fun onUpdateIsPrivate(isPrivate: Boolean) {
        uiState = uiState.copy(isPrivate = isPrivate)
    }

    fun handleEvent(event: ImportEvent) {
        when (event) {
            is ImportEvent.LoadDemoSets -> loadDemoSets()
            is ImportEvent.ImportPrivateDemoSet -> onImportPrivateDemoSet(
                event.accessCode,
                event.onSuccess,
                event.onFailure
            )

            is ImportEvent.ImportPublicDemoSet -> onImportPublicDemoSet(
                event.demoSetId,
                event.onSuccess,
                event.onFailure
            )

            is ImportEvent.PublishPlan -> onPublishPlan(
                event.planId,
                event.targetDemoId,
                event.isPrivate,
                event.onSuccess,
                event.onFailure
            )

            is ImportEvent.CheckPublishFeature -> onCheckPublishFeature(event.onSuccess, event.onFailure)
            is ImportEvent.UpdateAccessCode -> onUpdateAccessCode(event.accessCode)
            is ImportEvent.UpdateSelectedPlan -> onUpdateSelectedPlan(event.plan)
            is ImportEvent.UpdateTargetDemoId -> onUpdateTargetDemoId(event.targetDemoId)
            is ImportEvent.UpdateIsPrivate -> onUpdateIsPrivate(event.isPrivate)
        }
    }
}

@Composable
fun rememberImportController(
    scope: CoroutineScope,
    context: PageContext,
): ImportController {
    val listDemoSets = injectAndRemember<ListDemoSetsUseCase>()
    val importPublicDemoSet = injectAndRemember<ImportPublicDemoSetUseCase>()
    val importPrivateDemoSet = injectAndRemember<ImportPrivateDemoSetUseCase>()
    val publishPlan = injectAndRemember<PublishPlanDefinitionUseCase>()
    val checkFeatureEnabled = injectAndRemember<CheckFeatureEnabledUseCase>()

    return remember(scope, context) {
        ImportController(
            scope = scope,
            context = context,
            listDemoSetsUseCase = listDemoSets,
            importPublicDemoSetUseCase = importPublicDemoSet,
            importPrivateDemoSetUseCase = importPrivateDemoSet,
            publishPlanDefinitionUseCase = publishPlan,
            checkFeatureEnabledUseCase = checkFeatureEnabled,
        )
    }
}
